import { formatDateNumber } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import type { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import type { ParticipantNote } from '@seen/core/models/participantNote'
import type { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { typographyVariants } from '@seen/ui'
import { format, parseISO } from 'date-fns'
import type { JSX } from 'react'
import { Logo } from '#src/components/core/Logo.tsx'
import { useRead } from '#src/hooks/index.ts'
import { useStableAttachmentUrl } from '#src/hooks/useStableAttachmentUrl.ts'

// Print-only clinical-document header. Hidden on screen and revealed only in
// print via `print:block`, so the browser's native Cmd+P and the in-app Print
// button produce the same clean output (the web header bar is `print:hidden`).
export function VisitNotePrintHeader({
  note,
  participant,
  organization,
}: {
  note: ParticipantNote
  participant: Participant | undefined
  organization: ExtendedOrganization | null | undefined
}): JSX.Element {
  const { data: signer } = useRead(ExtendedPractitioner, note.signerReference)
  const photoUrl = useStableAttachmentUrl(participant?.photoUrl)

  return (
    <div className="hidden print:block">
      <VisitNotePrintHeaderView
        participantName={participant?.name}
        nickname={participant?.nickname}
        birthDate={participant?.birthDate}
        photoUrl={photoUrl}
        noteDate={note.created}
        signedOn={note.signedOn}
        signerName={signer?.name ?? referenceDisplay(note.signerReference)}
        organizationName={organization?.name}
        organizationPhone={organization?.primaryPhoneNumber}
        organizationFax={organization?.primaryFaxNumber}
      />
    </div>
  )
}

export interface VisitNotePrintHeaderViewProps {
  participantName?: string
  // Shown after the name in parentheses, matching the facesheet's PatientName.
  nickname?: string
  // ISO date (e.g. "1950-11-27"); rendered as MM/DD/YYYY.
  birthDate?: string
  photoUrl?: string
  // ISO datetime; rendered as "Mon D, YYYY" (no time of day).
  noteDate: string
  signedOn?: string
  signerName?: string
  organizationName?: string
  organizationPhone?: string
  organizationFax?: string
}

// US phone formatter: strips the leading country-code 1 and renders
// (XXX) XXX-XXXX. Falls back to the raw value for anything non-standard.
function formatUsPhone(raw: string | undefined): string | undefined {
  if (!raw) {
    return undefined
  }
  const digits = raw.replace(/\D/g, '')
  const local =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (local.length !== 10) {
    return raw
  }
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
}

const DOC_DATE_FORMAT = 'MMM d, yyyy'

// Pure presentational header — all resolved values passed in. Kept hook-free so
// it renders standalone in Storybook and tests.
export function VisitNotePrintHeaderView({
  participantName,
  nickname,
  birthDate,
  photoUrl,
  noteDate,
  signedOn,
  signerName,
  organizationName,
  organizationPhone,
  organizationFax,
}: VisitNotePrintHeaderViewProps): JSX.Element {
  const phone = formatUsPhone(organizationPhone)
  const fax = formatUsPhone(organizationFax)
  const contactLine = [phone && `Tel: ${phone}`, fax && `Fax: ${fax}`]
    .filter(Boolean)
    .join('  ·  ')

  return (
    <header className="pt-2 text-foreground">
      {/* Letterhead: logo + organization on the left, contact on one line. */}
      <div className="flex items-center justify-between gap-4 border-b border-border pb-2">
        <div className="flex items-center gap-2">
          <Logo size={18} className="shrink-0" />
          <span className={typographyVariants({ variant: 'h6' })}>
            {organizationName ?? 'Seen Health'}
          </span>
        </div>
        {contactLine && (
          <span className="text-xs text-muted-foreground">{contactLine}</span>
        )}
      </div>

      {/* Patient identification band: identity on the left, document metadata
          on the right. */}
      <div className="flex items-center justify-between gap-6 pt-4">
        <div className="flex items-center gap-3">
          {photoUrl && (
            <img
              src={photoUrl}
              alt=""
              className="size-10 rounded-full border border-border object-cover"
            />
          )}
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold">
              {participantName}
              {nickname && (
                <span className="text-foreground-secondary"> ({nickname})</span>
              )}
            </span>
            {birthDate && (
              <span className="text-sm text-muted-foreground">
                DOB: {formatDateNumber(birthDate)}
              </span>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Date:</dt>
          <dd className="font-medium tabular-nums">
            {format(parseISO(noteDate), DOC_DATE_FORMAT)}
          </dd>
          <dt className="text-muted-foreground">Signed:</dt>
          <dd className="font-medium">
            {signedOn
              ? `${format(parseISO(signedOn), DOC_DATE_FORMAT)}${
                  signerName ? ` by ${signerName}` : ''
                }`
              : 'Not signed'}
          </dd>
        </dl>
      </div>

      <div className="mt-4 border-b border-border" />
    </header>
  )
}
